import { AbstractControl, FormGroup } from '@angular/forms';
import { take } from 'rxjs';

import { CipoErrorModel, CipoErrorWithPropModel } from '@models/error/cipo-error.model';

/**
 * Moves per-property server errors onto the matching controls, so a rejected save shows on the
 * field instead of only in a toast.
 *
 * None of the module configuration forms validate uniqueness client-side — the server is the only thing that
 * knows a name is taken — so without this the user sees a toast over a form that still looks valid.
 *
 * The lookup ignores case: the server raises these with `nameof(model.Name)`, and whether that
 * reaches the client as `Name` or `name` depends on which serializer handles the response.
 */
export function applyServerErrors(form: FormGroup, error: unknown): boolean {
  const messages = (error as { error?: { messages?: CipoErrorWithPropModel | CipoErrorModel[] } })?.error?.messages;

  // A bare array is a general failure with no property attached; the toast already covers it.
  if (!messages || Array.isArray(messages)) {
    return false;
  }

  const controlsByLowerName = new Map(Object.keys(form.controls).map(name => [name.toLowerCase(), name]));
  const flagged: AbstractControl[] = [];

  Object.keys(messages).forEach(key => {
    const controlName = controlsByLowerName.get(key.toLowerCase());
    const control = controlName ? form.get(controlName) : null;
    if (!control) {
      return;
    }
    control.setErrors({ custom: messages[key].map(message => message.message).join(' ') });
    flagged.push(control);
  });

  if (flagged.length) {
    form.valueChanges
      .pipe(take(1))
      .subscribe(() => flagged.forEach(control => control.updateValueAndValidity({ emitEvent: false })));
  }

  return flagged.length > 0;
}
